import React, { useEffect, useState } from 'react';
import { CChart } from '@coreui/react-chartjs';
import { CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react';
import { Layout } from '../../components/Layout';
import { Language } from '../../language/English';
import { StatsService, StatItem } from './Api/StatsService';

const StatsChart = ({ 
    title, 
    description, 
    stats, 
    loading,
    error,
    label1 = Language.DOCUMENTS,
    label2 = Language.DRAFTS
}: { 
    title: string; 
    description: string; 
    stats: StatItem[]; 
    loading: boolean;
    error?: string | null;
    label1?: string;
    label2?: string;
}) => {
    const labels = stats.map((item) => item.date);
    const paidData = stats.map((item: any) => item.paid);
    const unpaidData = stats.map((item: any) => item.unpaid || item.total);

    return (
        <CCard className="mb-4 shadow-sm">
            <CCardHeader className="bg-white py-3">
                <h5 className="mb-0">
                    <strong>{title}</strong> 
                    <span className="text-muted fw-normal ms-2">{description}</span>
                </h5>
            </CCardHeader>
            <CCardBody className="p-4">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{Language.LOADING_}</span>
                        </div>
                        <p className="mt-2 text-muted">{Language.LOADING_}</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-5">
                        <div className="text-danger mb-3">
                            <i className="cil-warning" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <h6 className="text-danger">{error}</h6>
                        <p className="text-muted small">{Language.SOMETHING_IS_WRONG}</p>
                    </div>
                ) : (
                    <div style={{ height: '480px' }}>
                        <CChart
                            type="bar"
                            height={480}
                            data={{
                                labels: labels,
                                datasets: [
                                    {
                                        label: label2,
                                        backgroundColor: '#87CEEB', 
                                        data: unpaidData,
                                        barPercentage: 1,
                                    },
                                    {
                                        label: label1,
                                        backgroundColor: '#003366',
                                        data: paidData,
                                        barPercentage: 1,
                                    },
                                ],
                            }}
                            options={{
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'top',
                                    },
                                    tooltip: {
                                        enabled: true,
                                        position: 'average',
                                        mode: 'index',
                                        intersect: false,
                                        callbacks: {
                                            footer: (tooltipItems: any) => {
                                                let sum = 0;
                                                tooltipItems.forEach(function (tooltipItem: any) {
                                                    sum += (tooltipItem.raw || 0);
                                                });
                                                return Language.TOTAL + ': ' + sum;
                                            },
                                        },
                                    },
                                },
                                responsive: true,
                                scales: {
                                    x: {
                                        stacked: true,
                                        grid: {
                                            display: false,
                                        },
                                    },
                                    y: {
                                        stacked: true,
                                        beginAtZero: true,
                                        grid: {
                                            color: '#f0f0f0',
                                        },
                                        ticks: {
                                            
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                )}
            </CCardBody>
        </CCard>
    );
};

const Stats = () => {
    const [dailyDocStats, setDailyDocStats] = useState<StatItem[]>([]);
    const [monthlyDocStats, setMonthlyDocStats] = useState<StatItem[]>([]);
    const [dailyPayStats, setDailyPayStats] = useState<StatItem[]>([]);
    const [monthlyPayStats, setMonthlyPayStats] = useState<StatItem[]>([]);
    
    const [dailyDocLoading, setDailyDocLoading] = useState(true);
    const [monthlyDocLoading, setMonthlyDocLoading] = useState(true);
    const [dailyPayLoading, setDailyPayLoading] = useState(true);
    const [monthlyPayLoading, setMonthlyPayLoading] = useState(true);

    const [dailyDocError, setDailyDocError] = useState<string | null>(null);
    const [monthlyDocError, setMonthlyDocError] = useState<string | null>(null);
    const [dailyPayError, setDailyPayError] = useState<string | null>(null);
    const [monthlyPayError, setMonthlyPayError] = useState<string | null>(null);

    const statsService = new StatsService();

    useEffect(() => {
        const fetchStats = async () => {
            // Documents
            statsService.getDocumentStats().then(res => {
                if (res.isSuccess) {
                    setDailyDocStats(res.response);
                } else {
                    setDailyDocError(res.messageForUser);
                }
                setDailyDocLoading(false);
            }).catch(() => {
                setDailyDocError(Language.SOMETHING_IS_WRONG);
                setDailyDocLoading(false);
            });

            statsService.getDocumentMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyDocStats(res.response);
                } else {
                    setMonthlyDocError(res.messageForUser);
                }
                setMonthlyDocLoading(false);
            }).catch(() => {
                setMonthlyDocError(Language.SOMETHING_IS_WRONG);
                setMonthlyDocLoading(false);
            });

            // Payments
            statsService.getPaymentStats().then(res => {
                if (res.isSuccess) {
                    setDailyPayStats(res.response);
                } else {
                    setDailyPayError(res.messageForUser);
                }
                setDailyPayLoading(false);
            }).catch(() => {
                setDailyPayError(Language.SOMETHING_IS_WRONG);
                setDailyPayLoading(false);
            });

            statsService.getPaymentMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyPayStats(res.response);
                } else {
                    setMonthlyPayError(res.messageForUser);
                }
                setMonthlyPayLoading(false);
            }).catch(() => {
                setMonthlyPayError(Language.SOMETHING_IS_WRONG);
                setMonthlyPayLoading(false);
            });
        };

        fetchStats();
    }, []);

    return (
        <Layout>
            <CRow>
                {/* Document Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.DOCUMENTS} 
                        description={Language.LAST_40_DAYS_DRAFTS_VS_DOCUMENTS} 
                        stats={dailyDocStats} 
                        loading={dailyDocLoading} 
                        error={dailyDocError}
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.DOCUMENTS} 
                        description={Language.LAST_14_MONTHS_DRAFTS_VS_DOCUMENTS} 
                        stats={monthlyDocStats} 
                        loading={monthlyDocLoading} 
                        error={monthlyDocError}
                    />
                </CCol>

                {/* Payment Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.PAYMENTS} 
                        description={Language.LAST_40_DAYS_PAYMENT_SUCCESS_VS_NOT_COMPLETED} 
                        stats={dailyPayStats} 
                        loading={dailyPayLoading}
                        error={dailyPayError}
                        label1={Language.PAYMENT_SUCCESS}
                        label2={Language.PAYMENT_NOT_COMPLETED} 
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.PAYMENTS} 
                        description={Language.LAST_14_MONTHS_PAYMENT_SUCCESS_VS_NOT_COMPLETED} 
                        stats={monthlyPayStats} 
                        loading={monthlyPayLoading}
                        error={monthlyPayError}
                        label1={Language.PAYMENT_SUCCESS}
                        label2={Language.PAYMENT_NOT_COMPLETED} 
                    />
                </CCol>
            </CRow>
        </Layout>
    );
};

export default Stats;
